import {
  ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip as RTooltip, LabelList, Legend, Cell,
} from "recharts";

/**
 * The dashboard's chart vocabulary, in one place.
 *
 * Every chart on this screen is built from these pieces rather than configuring
 * recharts at each call site, so the twenty-odd plots share one grid weight, one
 * axis colour, one tooltip and one bar thickness. Getting that consistency by
 * convention across six files does not hold; getting it from one module does.
 */

// ── Colour ───────────────────────────────────────────────────────────────────
/**
 * Series colours, in fixed order, for charts whose subject is *identity* —
 * attorney against corporate, say.
 *
 * Three, and only three. The order is fixed and never cycled: a hue belongs to
 * an entity, so filtering a series out must not repaint the ones that remain.
 * These three were checked as a set for colour-vision separation (every pair,
 * not just neighbours); a fourth hue could not be added without one pair
 * becoming hard to tell apart, so a chart that would need a fourth series folds
 * its tail into "Other" or splits into two charts instead.
 */
export const SERIES = ["#4f46e5", "#e87a4e", "#0d9488"];

/**
 * The colour for a chart with one series.
 *
 * Bars of plan names, payment modes, customer names — these categories have no
 * natural order, so they all wear slot 1. Shading them darker-where-bigger would
 * encode bar length twice and spend the only free channel on something the
 * length already says.
 */
export const ONE = SERIES[0];

// Ink and chrome. Text never wears a series colour; the mark beside it carries
// the identity.
export const INK = { strong: "#111827", body: "#6b7280", muted: "#9ca3af" };
const GRID = "#eef1f6";
const SURFACE = "#fff";

// ── Formatting ───────────────────────────────────────────────────────────────
export const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
export const num = (n) => Number(n || 0).toLocaleString("en-IN");

/** Axis ticks have to stay short or they collide; full figures live in tooltips. */
export const inrShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(v % 1e7 ? 1 : 0)}Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(v % 1e5 ? 1 : 0)}L`;
  if (Math.abs(v) >= 1e3) return `₹${Math.round(v / 1e3)}k`;
  return `₹${v}`;
};

// ── Shared chrome ────────────────────────────────────────────────────────────
const axis = {
  tick: { fill: INK.muted, fontSize: 11 },
  axisLine: { stroke: GRID },
  tickLine: false,
};

/* Solid hairlines only. A dashed grid reads as a threshold or a projection when
   it is just a grid, and horizontals are enough — verticals double the ink for
   nothing on a categorical axis. */
const grid = <CartesianGrid stroke={GRID} vertical={false} />;

/**
 * One tooltip for every chart here.
 *
 * Charts are read by pointing at them, so this is not optional decoration: the
 * axis carries round numbers and the tooltip carries the exact ones, which is
 * what lets the plot stay free of a label on every mark.
 */
const TipBox = ({ active, payload, label, fmt = inr, extra }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: SURFACE, border: "1px solid #e5e9f0", borderRadius: 10,
      padding: "9px 12px", boxShadow: "0 6px 20px rgba(15,23,42,0.1)", fontSize: 12,
    }}>
      <div style={{ color: INK.strong, fontWeight: 700, marginBottom: payload.length ? 5 : 0 }}>
        {label}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, flexShrink: 0 }} />
          <span style={{ color: INK.body }}>{p.name}</span>
          <span style={{ marginLeft: "auto", color: INK.strong, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
            {fmt(p.value)}
          </span>
        </div>
      ))}
      {extra && <div style={{ color: INK.muted, marginTop: 5, fontSize: 11 }}>{extra(payload[0]?.payload)}</div>}
    </div>
  );
};

const legendStyle = { fontSize: 11.5, color: INK.body, paddingBottom: 6 };

// ── Frames ───────────────────────────────────────────────────────────────────
export const ChartEmpty = ({ height = 220, message = "Nothing to plot for this period." }) => (
  <div style={{
    height, display: "flex", alignItems: "center", justifyContent: "center",
    color: INK.muted, fontSize: 12.5, background: "#fafbff",
    border: "1px dashed #e5e9f0", borderRadius: 10,
  }}>
    {message}
  </div>
);

/**
 * Revenue, or any single measure, over time.
 *
 * An area rather than a bar row: the reader's question is the shape of the trend,
 * and one series needs no legend — the card's own title says what is plotted.
 * The last point is labelled because "where are we now" is the one value worth
 * reading without hovering.
 */
export const TrendArea = ({ data, xKey = "label", yKey = "amount", height = 240, color = ONE, onPointClick }) => {
  if (!data?.length) return <ChartEmpty height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 18, right: 22, left: 4, bottom: 0 }}>
        <defs>
          {/* A wash, not a block. A saturated fill this large drowns the line
              that is actually carrying the shape. */}
          <linearGradient id={`fill-${yKey}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.16} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        {grid}
        <XAxis dataKey={xKey} {...axis} interval="preserveStartEnd" />
        <YAxis {...axis} width={54} tickFormatter={inrShort} />
        <RTooltip
          content={<TipBox />}
          cursor={{ stroke: "#cbd5e1", strokeWidth: 1 }}
        />
        <Area
          type="monotone" dataKey={yKey} name="Revenue"
          stroke={color} strokeWidth={2} fill={`url(#fill-${yKey})`}
          dot={{ r: 3, fill: color, stroke: SURFACE, strokeWidth: 2 }}
          /* Bigger than the dot it sits on: an 8px hit target is the difference
             between a tooltip that works and one that has to be hunted. */
          activeDot={{ r: 5, fill: color, stroke: SURFACE, strokeWidth: 2 }}
          onClick={onPointClick}
          isAnimationActive={false}
        >
          <LabelList
            dataKey={yKey}
            position="top"
            content={({ x, y, value, index }) => (
              index === data.length - 1 ? (
                <text x={x} y={y - 8} textAnchor="middle" fill={INK.strong} fontSize={11} fontWeight={700}>
                  {inrShort(value)}
                </text>
              ) : null
            )}
          />
        </Area>
      </AreaChart>
    </ResponsiveContainer>
  );
};

/**
 * Magnitude across named things, laid out horizontally.
 *
 * Horizontal because the names here are customer names and plan names — long,
 * and unreadable rotated. The value rides the tip of each bar, so the plot needs
 * no vertical gridlines at all.
 */
export const RankedBars = ({
  data, labelKey = "name", valueKey = "value", height, fmt = inr,
  color = ONE, colorOf, nameWidth = 130, onBarClick, extra,
}) => {
  if (!data?.length) return <ChartEmpty height={height || 220} />;
  const h = height || Math.max(150, data.length * 34 + 30);
  return (
    <ResponsiveContainer width="100%" height={h}>
      <BarChart
        data={data} layout="vertical"
        margin={{ top: 4, right: 62, left: 4, bottom: 4 }}
        /* The 2px is the surface doing the separating — neighbours read apart
           because of the gap, not because a stroke was drawn around them. */
        barCategoryGap={6}
      >
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis type="number" {...axis} tickFormatter={fmt === inr ? inrShort : num} />
        <YAxis
          type="category" dataKey={labelKey} {...axis}
          width={nameWidth} interval={0}
        />
        <RTooltip content={<TipBox fmt={fmt} extra={extra} />} cursor={{ fill: "rgba(79,70,229,0.05)" }} />
        <Bar
          dataKey={valueKey} name="Value"
          /* Capped rather than filling the band: the leftover is the air that
             keeps a dense list readable. */
          maxBarSize={22}
          radius={[0, 4, 4, 0]}
          onClick={onBarClick}
          isAnimationActive={false}
          cursor={onBarClick ? "pointer" : "default"}
          fill={color}
        >
          {/* Per-bar colour only where the bars mean different things — a type,
              a status. Never shaded by their own value. */}
          {colorOf && data.map((d, i) => (
            <Cell key={i} fill={colorOf(d, i)} />
          ))}
          <LabelList
            dataKey={valueKey} position="right"
            style={{ fill: INK.strong, fontSize: 11, fontWeight: 700 }}
            formatter={(v) => (fmt === inr ? inrShort(v) : num(v))}
          />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * Two or three named series over the same categories.
 *
 * Grouped, not stacked, and never two y-axes: measures of different scale get
 * their own chart rather than a second scale whose alignment would invent a
 * relationship the data does not have.
 */
export const GroupedBars = ({
  data, xKey = "label", series, height = 250, fmt = inr, horizontal = false, nameWidth = 130,
}) => {
  if (!data?.length || !series?.length) return <ChartEmpty height={height} />;
  const common = (
    <>
      <RTooltip content={<TipBox fmt={fmt} />} cursor={{ fill: "rgba(79,70,229,0.05)" }} />
      {/* Always present at two or more series: identity must never rest on
          colour-matching alone. */}
      <Legend wrapperStyle={legendStyle} iconType="circle" iconSize={8} />
      {series.map((s, i) => (
        <Bar
          key={s.key} dataKey={s.key} name={s.label}
          fill={SERIES[i % SERIES.length]}
          maxBarSize={20}
          radius={horizontal ? [0, 4, 4, 0] : [4, 4, 0, 0]}
          isAnimationActive={false}
        />
      ))}
    </>
  );
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={data} layout={horizontal ? "vertical" : "horizontal"}
        margin={{ top: 8, right: 20, left: 4, bottom: 0 }}
        barCategoryGap={horizontal ? 8 : "22%"}
        barGap={2}
      >
        {horizontal ? <CartesianGrid stroke={GRID} horizontal={false} /> : grid}
        {horizontal ? (
          <>
            <XAxis type="number" {...axis} tickFormatter={fmt === inr ? inrShort : num} />
            <YAxis type="category" dataKey={xKey} {...axis} width={nameWidth} interval={0} />
          </>
        ) : (
          <>
            <XAxis dataKey={xKey} {...axis} interval="preserveStartEnd" />
            <YAxis {...axis} width={54} tickFormatter={fmt === inr ? inrShort : num} />
          </>
        )}
        {common}
      </BarChart>
    </ResponsiveContainer>
  );
};

/**
 * A single ratio against its limit.
 *
 * Deliberately not a two-slice pie, which is the hardest possible way to read
 * one percentage. The track is the same hue as the fill so the pair reads as one
 * measure.
 */
export const Meter = ({ label, value, total, color = ONE, fmt = num }) => {
  const pct = Number(total) > 0 ? Math.round((Number(value) / Number(total)) * 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 12.5, color: INK.body }}>{label}</span>
        <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: INK.strong, fontVariantNumeric: "tabular-nums" }}>
          {fmt(value)}
        </span>
        <span style={{ fontSize: 11, color: INK.muted, minWidth: 34, textAlign: "right" }}>{pct}%</span>
      </div>
      <div style={{ height: 7, borderRadius: 99, background: `${color}1a`, overflow: "hidden" }}>
        <div style={{ width: `${Math.min(100, pct)}%`, height: "100%", borderRadius: 99, background: color, transition: "width .5s ease" }} />
      </div>
    </div>
  );
};

/**
 * The table behind a chart.
 *
 * Present on every chart card, because a contrast warning on the lighter series
 * colours obliges a route to the numbers that does not depend on reading a hue —
 * and because "what exactly was March" is a fair question a plot cannot answer.
 */
export const DataTable = ({ columns, rows, maxHeight = 260 }) => {
  if (!rows?.length) return <ChartEmpty height={120} />;
  const th = {
    textAlign: "left", padding: "7px 10px", color: INK.muted, fontWeight: 600, fontSize: 10.5,
    textTransform: "uppercase", letterSpacing: "0.4px", borderBottom: "1px solid #f1f5f9",
    background: "#fafbff", position: "sticky", top: 0, zIndex: 1,
  };
  const td = { padding: "8px 10px", borderBottom: "1px solid #f8faff", fontSize: 12 };
  return (
    <div className="db-sc" style={{ maxHeight, overflowY: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} style={{ ...th, textAlign: c.align || "left" }}>{c.title}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="db-tr">
              {columns.map((c) => (
                <td key={c.key} style={{
                  ...td, textAlign: c.align || "left",
                  ...(c.align === "right" ? { fontVariantNumeric: "tabular-nums", fontWeight: 600, color: INK.strong } : { color: INK.body }),
                }}>
                  {c.render ? c.render(r) : r[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** A chart card, with the same head as the rest of the dashboard. */
export const ChartCard = ({ title, hint, right, children, style }) => (
  <div style={{
    background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05),0 4px 18px rgba(0,0,0,0.04)", padding: 20,
    ...style,
  }}>
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
      <span style={{ fontSize: 14, fontWeight: 700, color: INK.strong }}>{title}</span>
      {hint && <span style={{ fontSize: 11, color: INK.muted }}>{hint}</span>}
      {right && <div style={{ marginLeft: "auto" }}>{right}</div>}
    </div>
    {children}
  </div>
);

/** A small row of pill buttons — the one filter pattern used on this screen. */
export const PillRow = ({ options, value, onChange }) => (
  <div style={{ display: "inline-flex", gap: 6 }}>
    {options.map((o) => (
      <button
        key={o.key}
        onClick={() => onChange(o.key)}
        style={{
          padding: "4px 13px", borderRadius: 20, fontSize: 11.5, fontWeight: 600,
          cursor: "pointer", whiteSpace: "nowrap",
          border: `1px solid ${value === o.key ? "#c7d2fe" : "#e5e9f0"}`,
          background: value === o.key ? "#eef2ff" : "#fff",
          color: value === o.key ? ONE : INK.body,
        }}
      >
        {o.label}
      </button>
    ))}
  </div>
);
